/**
 * QR Code utility for Visit Prep Document
 * Uses the `qrcode` npm package to generate QR codes as data URLs
 * Run: npm install qrcode
 */
import { useState, useEffect, useRef } from 'react'
import { useI18n } from '../i18n'

// Dynamic import so build doesn't fail if not yet installed
let QRCodeLib = null
const loadQRLib = async () => {
  if (QRCodeLib) return QRCodeLib
  try {
    QRCodeLib = await import('qrcode')
    return QRCodeLib
  } catch {
    console.warn('[QR] qrcode library not available')
    return null
  }
}

/**
 * Generate QR code as base64 data URL (PNG)
 * @param {string} text - Text to encode
 * @param {object} options - QR options
 * @returns {Promise<string|null>} Data URL or null
 */
export async function generateQRDataURL(text, options = {}) {
  const lib = await loadQRLib()
  if (!lib) return null
  try {
    return await lib.toDataURL(text, {
      width: options.size || 200,
      margin: options.margin || 2,
      color: {
        dark: options.color || '#15354F',
        light: options.background || '#FFFFFF',
      },
      errorCorrectionLevel: 'M',
      ...options,
    })
  } catch (err) {
    console.error('[QR] Generation failed:', err)
    return null
  }
}

/**
 * React component that renders a QR code
 */
export function QRCodeDisplay({ value, size = 140, color = '#15354F', className = '' }) {
  const { t } = useI18n()
  const [dataUrl, setDataUrl] = useState(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!value) return
    generateQRDataURL(value, { size, color })
      .then(url => {
        if (url) setDataUrl(url)
        else setError(true)
      })
      .catch(() => setError(true))
  }, [value, size, color])

  if (error || !dataUrl) {
    // Fallback: styled placeholder with URL text
    return (
      <div
        className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 ${className}`}
        style={{ width: size, height: size }}
      >
        <svg width={size * 0.3} height={size * 0.3} viewBox="0 0 24 24" fill="none" stroke="#CBD5E1" strokeWidth="1.5">
          <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="4" height="4" />
          <line x1="21" y1="14" x2="21" y2="21" /><line x1="14" y1="21" x2="21" y2="21" />
        </svg>
        <p className="text-[8px] text-gray-400 mt-1 text-center px-1">{t('common.qrCode')}</p>
      </div>
    )
  }

  return (
    <img
      src={dataUrl}
      alt="QR Code"
      className={`rounded-lg ${className}`}
      style={{ width: size, height: size }}
    />
  )
}

export default QRCodeDisplay
